/**
 * Action layer for `StarterDashboards.tsx` (S21) — split out to keep the
 * component presentational. Owns the run/blank async flows: dry-run a pack's
 * templates (`starterPreflight`), create the dashboard only once at least
 * one tile passed, add the passing tiles, or (Blank) create an empty
 * dashboard and hand off to the parent to open Add Tile.
 */
import { useState } from 'react';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { useDashboardStore } from '../store/dashboardStore';
import type { DashboardEngineId } from '../types/dashboardTypes';
import type { DashboardTemplateCapability } from '../templates/templateTypes';
import {
  runTemplatePreview,
  starterPreflight,
  STARTER_PACK_LABELS,
  type StarterPack,
} from '../services/starterPreflight';

export function useStarterDashboardActions(args: {
  connectionId: string;
  engine: DashboardEngineId | undefined;
  database: string | undefined;
  serverVersion: string | undefined;
  capabilities: DashboardTemplateCapability[];
  onDashboardCreated: (dashboardId: string, opts?: { openAddTile?: boolean }) => void;
}) {
  const { track } = useAnalytics();
  const [running, setRunning] = useState<StarterPack | 'blank' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { connectionId, engine, database, serverVersion, capabilities, onDashboardCreated } = args;

  const runPack = async (pack: StarterPack) => {
    if (!engine || !connectionId || running) return;
    setRunning(pack);
    setError(null);
    try {
      const preflight = await starterPreflight({
        pack,
        engine,
        serverVersion,
        capabilities,
        connectionId,
        database,
        runPreview: (template) => runTemplatePreview(template, connectionId, database),
      });
      if (preflight.tiles.length === 0) {
        setError(
          preflight.candidateCount === 0
            ? `No ${STARTER_PACK_LABELS[pack]} templates apply to this connection yet — try a different pack, or start blank.`
            : `None of the ${STARTER_PACK_LABELS[pack]} checks could run on this connection — try a different pack, or start blank.`,
        );
        return;
      }
      const created = await useDashboardStore.getState().create(STARTER_PACK_LABELS[pack]);
      if (!created.ok) {
        setError(created.detail ?? 'Could not create the dashboard.');
        return;
      }
      for (const tile of preflight.tiles) {
        // Sequential: each addTile bumps the dashboard's revision — the
        // layout was already stacked gap-free by starterPreflight.
        // eslint-disable-next-line no-await-in-loop
        await useDashboardStore.getState().addTile(created.value.id, tile);
      }
      track('feature:dashboard_starter', { pack });
      onDashboardCreated(created.value.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setRunning(null);
    }
  };

  const createBlank = async () => {
    if (running) return;
    setRunning('blank');
    setError(null);
    try {
      const created = await useDashboardStore.getState().create('New dashboard');
      if (!created.ok) {
        setError(created.detail ?? 'Could not create the dashboard.');
        return;
      }
      track('feature:dashboard_starter', { pack: 'blank' });
      onDashboardCreated(created.value.id, { openAddTile: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setRunning(null);
    }
  };

  return { running, error, runPack, createBlank };
}
